import { useState } from 'react';
import { POLONIA_CREST as logoUrl } from './clubIdentity';
import { Activity, Dumbbell, ShieldCheck, Trophy, Users, CalendarCheck2, Radio, Shield, Newspaper, ExternalLink } from 'lucide-react';
import { useI18n } from './I18nContext';
import { useData } from './DataContext';
import { ClubLogo } from './ClubLogo';
import { getNextOfficialFixture, officialOpponent, officialVenueLabel } from './officialFixtures';
import type { ModuleId } from './AppShell';
import { isPlayerRole, isStaffRole } from './staffAccess';

function average(values: number[]) {
  if (!values.length) return 0;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

export function Dashboard({ onNavigate }: { onNavigate: (module: ModuleId) => void }) {
  const { t } = useI18n();
  const { team, players, matches, relationships, officialResults, officialLeagueSyncedAt, refresh, loading, error } = useData();
  const [gdsfcRefreshing, setGdsfcRefreshing] = useState(false);
  const [gdsfcRefreshMessage, setGdsfcRefreshMessage] = useState('');

  if (loading) return <section className="metal-card loading-panel">{t.loading}…</section>;
  if (error) return <section className="metal-card error-panel">{error}</section>;

  const completed = matches.filter(
    (match) => match.goals_for !== null && match.goals_against !== null,
  );

  // V24.1.1 — Dashboard match totals must include verified/imported GDSFC FT results,
  // not only rows that exist in the internal `matches` table. Merge Polonia results
  // with locally managed matches and de-duplicate by date + opponent.
  const poloniaName = (value: string | null | undefined) =>
    String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const isPolonia = (value: string | null | undefined) =>
    poloniaName(value).includes('polonia glasgow');
  const matchKey = (date: string, opponent: string) =>
    `${String(date || '').slice(0, 10)}|${poloniaName(opponent)}`;

  const completedTeamMatches = new Map<string, { goalsFor: number; goalsAgainst: number }>();

  completed.forEach((match) => {
    completedTeamMatches.set(matchKey(match.match_date, match.opponent || ''), {
      goalsFor: Number(match.goals_for ?? 0),
      goalsAgainst: Number(match.goals_against ?? 0),
    });
  });

  officialResults.forEach((result) => {
    const homeIsPolonia = isPolonia(result.home_team);
    const awayIsPolonia = isPolonia(result.away_team);
    if (!homeIsPolonia && !awayIsPolonia) return;

    const opponent = homeIsPolonia ? result.away_team : result.home_team;
    completedTeamMatches.set(matchKey(result.match_date, opponent), {
      goalsFor: Number(homeIsPolonia ? result.home_goals : result.away_goals),
      goalsAgainst: Number(homeIsPolonia ? result.away_goals : result.home_goals),
    });
  });

  const matchesPlayed = completedTeamMatches.size;
  const wins = [...completedTeamMatches.values()].filter(
    (match) => match.goalsFor > match.goalsAgainst,
  ).length;
  const averageForm = players.length
    ? (players.reduce((sum, player) => sum + Number(player.current_form ?? 0), 0) / players.length)
        .toFixed(1)
    : '0.0';
  const fitness = average(players.map((player) => Number(player.fitness ?? 0)));
  const morale = average(players.map((player) => Number(player.morale ?? 0)));
  const chemistry = average(relationships.map((relationship) => Number(relationship.score ?? 50))) || 50;
  const readiness = Math.round(fitness * 0.42 + morale * 0.28 + chemistry * 0.3);
  const manualNextMatch = [...matches]
    .filter((match) => new Date(match.match_date).getTime() >= Date.now() - 86400000)
    .sort((a, b) => a.match_date.localeCompare(b.match_date))[0];

  const officialNextMatch = getNextOfficialFixture();
  const officialDate = officialNextMatch
    ? new Date(`${officialNextMatch.date}T23:59:59`)
    : null;
  const manualDate = manualNextMatch ? new Date(manualNextMatch.match_date) : null;

  const useManualMatch = Boolean(
    manualNextMatch &&
    (!officialDate || (manualDate && manualDate.getTime() <= officialDate.getTime()))
  );

  const nextOpponent = useManualMatch
    ? manualNextMatch?.opponent || ''
    : officialNextMatch
      ? officialOpponent(officialNextMatch)
      : '';

  const manualMeta = manualNextMatch
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(`ecc_live_state_${manualNextMatch.id}`) || '{}');
        } catch {
          return {};
        }
      })()
    : {};

  const nextReferee = useManualMatch
    ? manualMeta.referee || 'TBA'
    : officialNextMatch?.referee || 'TBA';

  const playerMode = isPlayerRole(team?.role) || isStaffRole(team?.role);
  const latestOfficialResults = [...officialResults].sort((a,b) => b.match_date.localeCompare(a.match_date)).slice(0, 5);

  const gdsfcSyncAge = (() => {
    if (!officialLeagueSyncedAt) return 'No sync timestamp';
    const synced = new Date(officialLeagueSyncedAt).getTime();
    if (!Number.isFinite(synced)) return 'Sync time unavailable';
    const minutes = Math.max(0, Math.floor((Date.now() - synced) / 60000));
    if (minutes < 1) return 'Last synced just now';
    if (minutes === 1) return 'Last synced 1 min ago';
    if (minutes < 60) return `Last synced ${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    return `Last synced ${hours}h ${minutes % 60}m ago`;
  })();

  const refreshGdsfcData = async () => {
    if (gdsfcRefreshing) return;
    setGdsfcRefreshing(true);
    setGdsfcRefreshMessage('Refreshing…');
    try {
      await refresh();
      setGdsfcRefreshMessage('App data refreshed');
    } catch {
      setGdsfcRefreshMessage('Refresh failed');
    } finally {
      setGdsfcRefreshing(false);
      window.setTimeout(() => setGdsfcRefreshMessage(''), 3500);
    }
  };

  const metrics = [
    [String(players.length), t.squad, Users, 'players'],
    [String(matchesPlayed), t.matchesPlayed, Activity, 'matches'],
    [String(wins), t.wins, ShieldCheck, 'matches'],
    [String(averageForm), t.averageForm, Dumbbell, 'analysis'],
  ] as const;

  return (
    <div className="dashboard-grid">
      <section className="command-hero metal-card">
        <div className="hero-crest">
          <img src={logoUrl} alt="Polonia Glasgow AFC" />
        </div>
        <div className="hero-copy">
          <p className="eyebrow">{t.subtitle}</p>
          <h1>{team?.name || team?.club_name || 'POLONIA GLASGOW AFC'}</h1>
          <div className="hero-meta">
            <span>{t.season}: {team?.season || '2026/27'}</span>
            <span>{playerMode ? 'PLAYER VIEW' : t.headCoach}</span>
            <span>Only Sport Ltd Premiership</span>
          </div>
        </div>
        <div className="hero-status">
          <span className="status-light" />
          {t.liveData.toUpperCase()}
        </div>
      </section>

      {!playerMode && <button type="button" className="metal-card v24-command-launch" onClick={()=>onNavigate('operations')}>
        <div><p className="eyebrow">V24 · FOOTBALL OPERATIONS</p><h2>Coach Command Centre</h2><p>Availability · Readiness · Training Load · Trends · Reports · Scouting</p></div>
        <div className="v24-command-arrow">→</div>
      </button>}
      {playerMode && <button type="button" className="metal-card v24-command-launch" onClick={()=>onNavigate('operations')}>
        <div><p className="eyebrow">TEAM OPERATIONS</p><h2>My Availability & Readiness</h2><p>Confirm match status and complete your 10-second check-in.</p></div><div className="v24-command-arrow">→</div>
      </button>}

      <section className="metal-card sunday-control-strip">
        <div>
          <p className="eyebrow">{t.sundayGoLive}</p>
          <strong>{t.matchdayControl}</strong>
        </div>
        <div className="sunday-control-actions">
          <button type="button" onClick={() => onNavigate('lineup')}>
            <Shield size={18} /><span>{t.lineup}</span>
          </button>
          <button type="button" className="primary" onClick={() => onNavigate('matchday')}>
            <CalendarCheck2 size={18} /><span>{t.matchday}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              try { localStorage.setItem('ecc_match_centre_requested_view', 'live'); } catch {}
              onNavigate(playerMode ? 'matchday' : 'matches');
            }}
          >
            <Radio size={18} /><span>{t.liveMatch}</span>
          </button>
        </div>
      </section>

      <section className="metrics-grid">
        {metrics.map(([value, label, Icon, target]) => (
          <button
            type="button"
            className="metric-card metal-card smart-nav-card"
            key={label}
            onClick={() => onNavigate(target)}
            aria-label={`${label} →`}
          >
            <Icon size={24} />
            <strong>{value}</strong>
            <span>{label}</span>
          </button>
        ))}
      </section>

      {!playerMode && <button type="button" className="metal-card readiness-panel smart-nav-panel" onClick={() => onNavigate('intelligence')}>
        <div>
          <p className="eyebrow">MATCH INTELLIGENCE</p>
          <h2>{t.readiness}</h2>
          <div className="readiness-ring" style={{ '--readiness': `${readiness}%` } as React.CSSProperties}>
            <strong>{readiness}</strong><span>/100</span>
          </div>
        </div>
        <div className="readiness-bars">
          {[
            [t.fitness, fitness],
            [t.chemistry, chemistry],
            [t.morale, morale],
            [t.squadAvailability, players.filter((player) => Number(player.fitness ?? 0) >= 65).length
              ? Math.round(players.filter((player) => Number(player.fitness ?? 0) >= 65).length / players.length * 100)
              : 0],
          ].map(([label, value]) => (
            <div className="progress-row" key={String(label)}>
              <span>{label}</span>
              <div className="progress-track"><i style={{ width: `${value}%` }} /></div>
              <b>{value}</b>
            </div>
          ))}
        </div>
      </button>}

      <button type="button" className="metal-card next-match-card smart-nav-panel" onClick={() => onNavigate('matchday')}>
        <p className="eyebrow">{t.nextMatch}</p>
        <div className="match-versus dashboard-match-versus">
          <div className="dashboard-home-club"><img className="dashboard-home-logo" src={logoUrl} alt="" /><b>Polonia</b></div>
          <strong>VS</strong>
          <div className="dashboard-away-club">
            <ClubLogo club={nextOpponent} className="dashboard-opponent-logo" />
            <b>{nextOpponent || t.noData}</b>
          </div>
        </div>
        {useManualMatch && manualNextMatch ? (
          <div className="next-match-intelligence">
            <p>{new Date(manualNextMatch.match_date).toLocaleString()} · {manualNextMatch.competition || ''}</p>
            <span>{t.referee}: {nextReferee}</span>
            <span>{t.coachOverride}</span>
          </div>
        ) : officialNextMatch ? (
          <div className="next-match-intelligence">
            <p>{new Date(`${officialNextMatch.date}T12:00:00`).toLocaleDateString()} · {officialNextMatch.competition}</p>
            <span>{t.round} {officialNextMatch.round} · {officialVenueLabel(officialNextMatch)}</span>
            <span>{t.referee}: {nextReferee}</span>
            <span>{t.officialGdsfcFixture}</span>
          </div>
        ) : (
          <p>{t.noData}</p>
        )}
      </button>

      <section className="metal-card gdsfc-dashboard-news">
        <div className="section-title-line">
          <div><p className="eyebrow">GDSFC NEWS</p><h2>Latest league update</h2></div>
          <Newspaper size={24}/>
        </div>
        {latestOfficialResults.length ? (
          <div className="dashboard-result-feed">
            {latestOfficialResults.map((result) => (
              <div className="dashboard-result-row" key={result.source_key}>
                <span>{new Date(`${result.match_date}T12:00:00`).toLocaleDateString()}</span>
                <strong>{result.home_team}</strong>
                <b>{result.home_goals}–{result.away_goals}</b>
                <strong>{result.away_team}</strong>
              </div>
            ))}
          </div>
        ) : <p className="muted">No current GDSFC result feed yet.</p>}
        <div className="dashboard-news-actions">
          <button type="button" className="steel-button" onClick={refreshGdsfcData} disabled={gdsfcRefreshing}>
            {gdsfcRefreshing ? 'Refreshing…' : 'Refresh GDSFC data'}
          </button>
          <a className="steel-button" href="https://github.com/poLosHaQ/Elite-Coach-Cloud-2/actions/workflows/gdsfc-sync.yml" target="_blank" rel="noreferrer">
            <ExternalLink size={15}/> Force source sync
          </a>
          <button type="button" className="steel-button" onClick={() => onNavigate('league')}>League Centre</button>
          <a className="steel-button" href="https://www.instagram.com/gdsfc2008/" target="_blank" rel="noreferrer"><ExternalLink size={15}/> @gdsfc2008</a>
        </div>
        <p className="muted" style={{ marginBottom: 0, fontSize: '.74rem' }}>
          {gdsfcSyncAge}{gdsfcRefreshMessage ? ` · ${gdsfcRefreshMessage}` : ''}
        </p>
      </section>

      <section className="metal-card activity-card">
        <p className="eyebrow">{t.recentActivity}</p>
        {[
          [`${players.length} ${t.registeredPlayers}`, 'players'],
          [`${matchesPlayed} ${t.matchesPlayed}`, 'matches'],
          ...(!playerMode ? [[`${relationships.length} ${t.chemistry}`, 'harmony']] : []),
          [`${wins} ${t.wins}`, 'matches'],
        ].map(([item, target]) => (
          <button
            type="button"
            className="activity-row smart-activity-row"
            key={item}
            onClick={() => onNavigate(target as ModuleId)}
          >
            <Activity size={18} />
            <span>{item}</span>
            <b aria-hidden="true">›</b>
          </button>
        ))}
      </section>

      <button type="button" className="metal-card trophy-card smart-nav-panel" onClick={() => onNavigate('league')}>
        <Trophy size={44} />
        <p className="eyebrow">CLUB STATUS</p>
        <h2>Command Centre Active</h2>
        <p>Elite Coach Cloud V23.0 · Reference Stadium</p>
      </button>
    </div>
  );
}
